import React, { useRef, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import DOMPurify from 'dompurify';
import { useModalPositioning } from '../Utilities/ModalPositioning';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../CSS/Modal.css';

const CustomModal: React.FC<{
  show: boolean;
  onHide: () => void;
  element: any;
  position?: { top: number } | null;
}> = ({ show, onHide, element, position }) => {
  const hasPermissionLanguage =
    element.image_permission_language?.value?.trim();
  const hasDigitalAssetId = element.digital_asset_id?.value;

  // Check if we're in an iframe using your existing pattern
  const isInIframe = window.parent && window.parent !== window;

  const modalRef = useRef<HTMLDivElement>(null);
  useModalPositioning(position ?? null, show);

  // Only manage body scrolling and classes when in iframe
  useEffect(() => {
    if (isInIframe) {
      // Add iframe class to body
      document.body.classList.add('in-iframe');

      // Manage modal-open class for scrolling
      if (show) {
        document.body.classList.add('modal-open');
      } else {
        document.body.classList.remove('modal-open');
      }

      return () => {
        document.body.classList.remove('modal-open');
        document.body.classList.remove('in-iframe');
      };
    }
  }, [show, isInIframe]);

  const hasMeaningfulText = (richText: any): boolean => {
    if (!richText?.value) return false;
    const strippedText = DOMPurify.sanitize(richText.value, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    }).trim();
    return /^[\w\s]+$/.test(strippedText);
  };

  const modalKey = element.image_asset?.value?.[0]?.url || 'default';

  return (
    <div ref={modalRef}>
      <Modal
        key={modalKey}
        show={show}
        onHide={onHide}
        size="lg"
        aria-labelledby="modal-N100DF"
        role="dialog"
        tabIndex={-1}
        backdrop={true}
        keyboard={true}
        className="modal-fade in"
        style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
        centered={!position}
      >
        <Modal.Header className="modal-header">
          <button
            type="button"
            className="close"
            aria-label="Close"
            onClick={onHide}
            style={{
              background: 'none',
              border: 'none',
              color: '#000',
              opacity: 0.5,
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
          {element.title?.value && (
            <Modal.Title id="modal-N100DF" className="modal-title">
              {element.title.value}
            </Modal.Title>
          )}
        </Modal.Header>
        <Modal.Body>
          {element.image_asset?.value?.length > 0 && (
            <img
              src={element.image_asset.value[0].url}
              alt={element.image_asset.value[0].description || 'Image'}
              className="img-responsive center-block"
            />
          )}
        </Modal.Body>
        {(hasPermissionLanguage || hasDigitalAssetId) && (
          <Modal.Footer style={{ paddingTop: '0.5rem' }}>
            <div
              className="da-footer"
              style={{ marginTop: '0', paddingLeft: '0px' }}
            >
              <span className="daid" style={{ textAlign: 'left' }}>
                {hasMeaningfulText(element.image_permission_language) &&
                  DOMPurify.sanitize(element.image_permission_language?.value, {
                    ALLOWED_TAGS: [],
                    ALLOWED_ATTR: [],
                  }).trim()}
                {element.digital_asset_id?.value &&
                  ` [${element.digital_asset_id?.value}]`}
              </span>
            </div>
          </Modal.Footer>
        )}
      </Modal>
    </div>
  );
};

export default CustomModal;
